// useGalleryCardFieldFormatting.js

import { useCallback, useMemo } from 'react';
import { isEnumCompatibleFieldType, isRelationFieldType, normalizeSchemaFieldType } from 'utilities/FieldTypes';

import { getEnumOptionLabel } from 'utilities/EnumOptions';
import {
    DEFAULT_SUBTITLE_FIELD_KEY,
    LEGACY_SUBTITLE_FIELD_KEY,
    getNodeSubtitle,
    getViewIncludeSubtitle,
    isSubtitleFieldKey,
    isSubtitleFieldType,
} from 'utilities/NodeSubtitle';
import { normalizeNodelistKeys } from 'utilities/NodelistField';
import { extractRichTextPlainText } from 'utilities/RichText';
import { formatSignatureFieldDisplay } from 'utilities/SignatureFieldValue';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';
import { formatTemporalFieldValue, isFirestoreTimestampObject, isTemporalFieldType } from 'views/utils/temporalFieldFormat';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import useBoardNodelistHydration from 'views/board/hooks/useBoardNodelistHydration';

const PERSON_LABEL_KEYS = ['name', 'displayName', 'fullName', 'title', 'label', 'email'];
const STANDARD_FIELD_DEFINITIONS = [
    { key: 'autonumber', name: 'autonumber', label: 'ID', type: 'number' },
    { key: 'autokey', name: 'autokey', label: 'Key', type: 'string' },
    { key: 'title', name: 'title', label: 'Title', type: 'string' },
    { key: DEFAULT_SUBTITLE_FIELD_KEY, name: DEFAULT_SUBTITLE_FIELD_KEY, label: 'Subtitle', type: DEFAULT_SUBTITLE_FIELD_KEY },
    { key: 'tags', name: 'tags', label: 'Tags', type: 'tags' },
    { key: 'dateCreated', name: 'dateCreated', label: 'Date Created', type: 'datetime' },
    { key: 'dateModified', name: 'dateModified', label: 'Date Modified', type: 'datetime' },
];

const getObjectDisplayValue = (item) => {
    if (!item || typeof item !== 'object') return '';

    for (const key of PERSON_LABEL_KEYS) {
        const candidate = item?.[key];
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
        }
    }

    return '';
};

const useGalleryCardFieldFormatting = ({
    enforceExplicitProperties = false,
    view,
    typeDef,
    language,
    nodes = [],
}) => {
    const configuredFieldKeys = useMemo(() => (
        Array.isArray(view?.properties) ? view.properties.filter(Boolean) : []
    ), [view?.properties]);

    const fields = useMemo(() => (
        resolveDisplaySchemaFields(typeDef, { includeHidden: true })
    ), [typeDef]);

    const schemaFieldMap = useMemo(() => {
        const map = new Map();
        STANDARD_FIELD_DEFINITIONS.forEach((field) => {
            const key = field?.name || field?.key;
            if (key) map.set(key, field);
        });
        fields.forEach((field) => {
            const key = field?.name || field?.key;
            if (key) map.set(key, field);
        });
        return map;
    }, [fields]);

    const selectedSubtitleFieldKey = useMemo(() => (
        configuredFieldKeys.find((fieldKey) => {
            const fieldType = String(schemaFieldMap.get(fieldKey)?.type || '').toLowerCase();
            return isSubtitleFieldType(fieldType);
        }) || ''
    ), [configuredFieldKeys, schemaFieldMap]);

    const viewFieldKeys = useMemo(() => {
        const next = [...configuredFieldKeys];

        if (
            getViewIncludeSubtitle(view)
            && !next.includes(DEFAULT_SUBTITLE_FIELD_KEY)
            && !next.includes(LEGACY_SUBTITLE_FIELD_KEY)
        ) {
            next.push(DEFAULT_SUBTITLE_FIELD_KEY);
        }

        if (view?.includeCover && !next.includes('cover')) {
            next.push('cover');
        }

        return next;
    }, [configuredFieldKeys, view]);

    const showSubtitle = useMemo(() => {
        if (!enforceExplicitProperties) return true;

        return getViewIncludeSubtitle(view)
            || viewFieldKeys.includes(DEFAULT_SUBTITLE_FIELD_KEY)
            || viewFieldKeys.includes(LEGACY_SUBTITLE_FIELD_KEY)
            || Boolean(selectedSubtitleFieldKey);
    }, [enforceExplicitProperties, selectedSubtitleFieldKey, view, viewFieldKeys]);

    const showCover = useMemo(() => {
        if (!enforceExplicitProperties) return true;

        return Boolean(view?.includeCover)
            || viewFieldKeys.includes('cover')
            || viewFieldKeys.includes('coverUrl');
    }, [enforceExplicitProperties, view?.includeCover, viewFieldKeys]);

    const cardFieldKeys = useMemo(() => {
        const configured = viewFieldKeys.filter((field) => (
            field !== 'title'
            && field !== DEFAULT_SUBTITLE_FIELD_KEY
            && field !== LEGACY_SUBTITLE_FIELD_KEY
            && field !== selectedSubtitleFieldKey
            && field !== 'cover'
            && field !== 'coverUrl'
        ));

        if (Array.isArray(view?.properties)) {
            return configured;
        }

        // No explicit properties → show all ordinary schema fields (organic default).
        return fields
            .map((field) => String(field?.name || field?.key || '').trim())
            .filter((key) => {
                if (!key) return false;
                const normalized = key.toLowerCase();
                return normalized !== 'title'
                    && normalized !== 'cover'
                    && normalized !== 'coverurl';
            });
    }, [fields, selectedSubtitleFieldKey, view?.properties, viewFieldKeys]);

    const showNoteContents = useMemo(() => {
        if (!enforceExplicitProperties) return true;
        return cardFieldKeys.includes('contents');
    }, [cardFieldKeys, enforceExplicitProperties]);

    const resolveFieldValue = useCallback((node, fieldDef) => {
        if (!node || !fieldDef) return undefined;

        const fieldKey = fieldDef?.name || fieldDef?.key || '';
        const fieldType = String(fieldDef?.type || '').toLowerCase();

        if (!fieldKey) return undefined;

        if (fieldKey === 'title') return node?.title;
        if (fieldKey === 'autonumber') return node?.autonumber;
        if (fieldKey === 'autokey') return node?.autokey;
        if (isSubtitleFieldKey(fieldKey, fieldType)) return getNodeSubtitle(node);
        if (fieldKey === 'tags' || fieldType === 'tags') return node?.tags;
        if (fieldKey === 'dateCreated') return node?.dateCreated;
        if (fieldKey === 'dateModified') return node?.dateModified;

        return resolveSchemaFieldValue({
            item: node,
            fieldDef,
            typeKey: typeDef?.key,
            dataSection: typeDef?.dataSection,
        });
    }, [typeDef?.dataSection, typeDef?.key]);

    // Hydrate Relation card fields so titles display instead of raw node keys.
    const { resolveNodelistTitle } = useBoardNodelistHydration({
        nodes,
        schemaFieldMap,
        fieldKeys: cardFieldKeys,
        resolveFieldValue,
    });

    const formatFieldValue = useCallback((value, fieldDef, fieldKey) => {
        if (value == null || value === '') return '';
        const fallbackType = fieldKey === 'dateCreated' || fieldKey === 'dateModified'
            ? 'date'
            : '';
        const fieldType = normalizeSchemaFieldType(fieldDef?.type || fallbackType);
        const isTimestampObject = isFirestoreTimestampObject(value);

        if (fieldType === 'checklist') {
            const totalItems = Number(value?.totalItems) || 0;
            const completedItems = Number(value?.completedItems) || 0;
            if (totalItems <= 0) return '';
            return `${completedItems}/${totalItems}`;
        }
        if (fieldType === 'boolean') return value ? 'Yes' : 'No';
        if (isTemporalFieldType(fieldType) || isTimestampObject) {
            return formatTemporalFieldValue({
                value,
                fieldType,
                locale: 'en-US',
                dateOptions: {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                },
                dateTimeOptions: {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                },
            });
        }
        if (isEnumCompatibleFieldType(fieldType)) {
            return getEnumOptionLabel(fieldDef?.options || [], value, language);
        }
        if (fieldType === 'tags') {
            return Array.isArray(value) ? value.join(', ') : String(value);
        }
        if (fieldType === 'richtext') {
            return extractRichTextPlainText(value);
        }
        if (fieldType === 'signature') {
            return formatSignatureFieldDisplay(value);
        }
        if (isRelationFieldType(fieldDef) || fieldType === 'relation') {
            const keys = normalizeNodelistKeys(value);
            if (!keys.length) return '';
            return keys
                .map((key) => String(resolveNodelistTitle(key) || '').trim())
                .filter(Boolean)
                .join(', ');
        }
        if (Array.isArray(value)) {
            const formatted = value
                .map((item) => {
                    if (item == null || item === '') return '';
                    if (typeof item === 'object') {
                        return getObjectDisplayValue(item) || JSON.stringify(item);
                    }
                    return String(item);
                })
                .filter(Boolean);
            return formatted.join(', ');
        }
        if (typeof value === 'object') {
            return getObjectDisplayValue(value) || JSON.stringify(value);
        }
        return String(value);
    }, [language, resolveNodelistTitle]);

    const getNodeFieldCount = useCallback((node) => {
        if (!node) return 0;

        let count = 0;
        const subtitle = showSubtitle
            ? getNodeSubtitle(node).trim()
            : '';

        if (subtitle) count += 1;

        if (showNoteContents) {
            const noteContentsField = schemaFieldMap.get('contents');
            if (noteContentsField) {
                const noteContentsValue = resolveFieldValue(node, noteContentsField);
                const noteContents = formatFieldValue(noteContentsValue, noteContentsField, 'contents');
                if (noteContents) count += 1;
            }
        }

        cardFieldKeys.forEach((fieldKey) => {
            if (showNoteContents && fieldKey === 'contents' && schemaFieldMap.has('contents')) return;

            const fieldDef = schemaFieldMap.get(fieldKey) || null;
            const rawValue = fieldDef
                ? resolveFieldValue(node, fieldDef)
                : node?.[fieldKey];
            const displayValue = formatFieldValue(rawValue, fieldDef, fieldKey);
            if (displayValue) count += 1;
        });
        return count;
    }, [cardFieldKeys, formatFieldValue, resolveFieldValue, schemaFieldMap, showSubtitle, showNoteContents]);

    return {
        cardFieldKeys,
        showCover,
        showSubtitle,
        showNoteContents,
        schemaFieldMap,
        resolveFieldValue,
        formatFieldValue,
        getNodeFieldCount,
    };
};

export default useGalleryCardFieldFormatting;
